import { gainDbToLinear } from '../shared/track-gain.ts'
import type { Track } from '../shared/store/tracks.store.tsx'

const createFadeCurve = (length: number, isFadeIn: boolean): Float32Array => {
  const curve = new Float32Array(length)
  for (let i = 0; i < length; i++) {
    const t = i / (length - 1)
    curve[i] = isFadeIn ? 1 - Math.pow(1 - t, 2) : 1 - Math.pow(t, 2)
  }
  return curve
}

export const createFadeIn = (length: number): Float32Array =>
  createFadeCurve(length, true)
export const createFadeOut = (length: number): Float32Array =>
  createFadeCurve(length, false)

interface FadeConfig {
  clipGain: GainNode
  startAtSec: number
  playDuration: number
  fadeInPadding: number
  fadeOutPadding: number
}

export const applyFades = (config: FadeConfig): void => {
  const { clipGain, startAtSec, playDuration, fadeInPadding, fadeOutPadding } =
    config
  const stepMs = 10 // Step between volume changes in milliseconds

  const minFade = 0.005
  const fadeIn = Math.max(fadeInPadding, minFade)
  const fadeOut = Math.max(fadeOutPadding, minFade)

  clipGain.gain.setValueAtTime(0, startAtSec)
  const fadeInResolution = Math.max(2, Math.ceil((fadeIn * 1000) / stepMs))
  const fadeInCurve = createFadeIn(fadeInResolution)
  clipGain.gain.setValueCurveAtTime(fadeInCurve, startAtSec, fadeIn)

  const fadeOutStartTime = startAtSec + playDuration - fadeOut
  if (fadeOutStartTime > startAtSec) {
    clipGain.gain.setValueAtTime(1, fadeOutStartTime)
    const fadeOutResolution = Math.max(2, Math.ceil((fadeOut * 1000) / stepMs))
    const fadeOutCurve = createFadeOut(fadeOutResolution)
    clipGain.gain.setValueCurveAtTime(fadeOutCurve, fadeOutStartTime, fadeOut)
  }
}

export async function mixTracks(
  tracks: Track[],
  media: Record<string, AudioBuffer | undefined>,
  audioContext: AudioContext,
  totalDurationSec: number
): Promise<AudioBuffer> {
  const sampleRate = audioContext.sampleRate

  const offlineContext = new OfflineAudioContext(
    2,
    totalDurationSec * sampleRate,
    sampleRate
  )

  const masterGain = offlineContext.createGain()
  masterGain.connect(offlineContext.destination)

  tracks.forEach((track) => {
    if (track.isMuted || track.isForceMuted) return

    const trackGain = offlineContext.createGain()
    trackGain.gain.value = gainDbToLinear(track.volume)
    trackGain.connect(masterGain)

    track.clips.forEach((clip) => {
      const audioBuffer = media[clip.mediaId]
      if (!audioBuffer || clip.startAt === undefined) return

      const clipOffset = clip.startOffset
      const startAtSec = clip.startAt + clipOffset
      const durationSec = audioBuffer.duration - clipOffset + clip.endOffset

      const maxDuration = totalDurationSec - startAtSec
      const playDuration = Math.min(durationSec, maxDuration)

      const source = offlineContext.createBufferSource()
      source.buffer = audioBuffer

      const clipGain = offlineContext.createGain()
      source.connect(clipGain)
      clipGain.connect(trackGain)

      applyFades({
        clipGain,
        startAtSec,
        playDuration,
        fadeInPadding: clip.fadeIn,
        fadeOutPadding: clip.fadeOut,
      })

      source.start(startAtSec, clipOffset, playDuration)
    })
  })

  return await offlineContext.startRendering()
}
